import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import {
  FiCalendar,
  FiUser,
  FiFileText,
  FiLogOut,
  FiHome,
  FiChevronRight,
  FiX,
  FiMenu,
  FiBell
} from "react-icons/fi";
import {
  MdCases,
  MdEventNote,
  MdPerson,
  MdCalendarToday,
  MdVerifiedUser
} from "react-icons/md";

import UserAppointments from "./UserAppointmentDetails";
import UserProfile from "./UserProfile";
import UserHearings from "./UserHearings";
import Calendar from "./Calendar";
import UserCases from "./UserCases";
import UserAgreements from "./UserAgreements";
import UserAgreementSign from "./UserAgreementSign";

import "../styles/UserDashboard.css";

export default function UserDashboard() {
  const { token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // 👇 appointment id passed from BookAppointment redirect
  const openAppointmentId = location.state?.openAppointmentId || null;

  const [activeSection, setActiveSection] = useState(() => {
    return sessionStorage.getItem("user_active_section") || "appointments";
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const [openAgreementId, setOpenAgreementId] = useState(null);

  /* ======================
     LOAD USER FROM /auth/me
  ====================== */
  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!res.ok) throw new Error("Failed to load user");

        const data = await res.json();
        setUserData(data.user);

        // Fetch notifications (example - adjust endpoint as needed)
        fetchNotifications();
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);



  const fetchNotifications = async () => {
    try {
      if (!userData) return;

      const res = await fetch(
        `${API_BASE_URL}/api/appointments/my`,   // 🔥 IMPORTANT CHANGE
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) return;

      const data = await res.json();
      const appointments = data.appointments || [];


      const pendingMeetings = appointments.filter(app => {

        // 🔔 SHOW WHEN:

        // 1️⃣ Waiting for solicitor approval
        if (app.status === "PENDING") return true;

        // 2️⃣ Accepted but meeting not started yet
        if (
          app.status === "ACCEPTED" &&
          app.appointmentCallStatus === "NOT_STARTED"
        ) {
          return true;
        }

        return false;
      });


      setNotificationCount(pendingMeetings.length);

    } catch (err) {
      console.error("Notification fetch failed:", err);
    }
  };







  useEffect(() => {
    if (!token || !userData) return;

    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userData]);




  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleNavigation = (sectionId) => {
    setActiveSection(sectionId);
    sessionStorage.setItem("user_active_section", sectionId);
    setSidebarOpen(false);
  };

  const menuItems = [
    {
      id: "appointments",
      label: "My Appointments",
      icon: <FiCalendar />,
      badge: null
    },
    {
      id: "cases",
      label: "My Cases",
      icon: <MdCases />
    },
    {
      id: "hearings",
      label: "My Hearings",
      icon: <MdEventNote />
    },
    {
      id: "calendar",
      label: "My Calendar",
      icon: <MdCalendarToday />
    },
    // {
    //   id: "chat",
    //   label: "Messages",
    //   icon: <FiMessageCircle />,  
    //   badge: null
    // },
    {
      id: "agreements",          // 🔥 NEW
      label: "My Agreements",
      icon: <FiFileText />
    },
    {
      id: "profile",
      label: "My Profile",
      icon: <MdPerson />
    }
  ];

  if (loading) {
    return (
      <div className="luxury-loading-container">
        <div className="luxury-loading-spinner"></div>
        <p>Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="luxury-client-wrapper">
      {/* ======================
         MOBILE HEADER
      ====================== */}
      <header className="luxury-client-mobile-header">
        <button
          className="luxury-client-mobile-menu-btn"
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          {sidebarOpen ? <FiX size={20} /> : <FiMenu size={20} />}
        </button>

        <div className="luxury-client-mobile-brand">
          <div className="brand-icon-wrapper">
            <MdVerifiedUser className="brand-icon" size={22} />
          </div>
          <span>Client Portal</span>
        </div>

        <div className="luxury-client-mobile-actions">
          <button
            className="luxury-icon-btn"
            onClick={() => navigate("/")}
            title="Back to Home"
          >
            <FiHome size={18} />
          </button>

        </div>
      </header>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="luxury-client-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ======================
         LUXURY SIDEBAR
      ====================== */}
      <aside className={`luxury-client-sidebar ${sidebarOpen ? "open" : ""}`}>
        {/* Brand Section */}
        <div className="luxury-client-sidebar-brand">
          <div className="luxury-client-brand-content">
            <div className="brand-icon-wrapper">
              <MdVerifiedUser className="brand-icon" size={28} />
            </div>
            <div>
              <h2>Client Portal</h2>
              <p className="client-brand-subtitle">Legal Services Dashboard</p>
            </div>
          </div>
        </div>

        {/* User Profile Card */}
        <div className="luxury-client-user-card">
          <div className="luxury-client-avatar-container">
            {userData?.userProfile?.photo ? (
              <img
                src={userData.userProfile.photo}
                alt={userData.name}
                className="luxury-client-avatar"
              />
            ) : (
              <div className="luxury-avatar-fallback">
                {userData?.name?.charAt(0)?.toUpperCase() || "C"}
              </div>
            )}
            <div className="client-user-status-indicator online"></div>
          </div>

          <div className="luxury-client-user-info">
            <h3 className="client-user-name">
              {userData?.name || "Client"}
            </h3>
            <p className="client-user-email">
              {userData?.email || "client@example.com"}
            </p>

            <div className="client-user-role-tag">
              <FiUser size={12} />
              <span>Client Account</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="luxury-client-sidebar-nav">
          <div className="client-nav-header">
            <span className="client-nav-title">NAVIGATION</span>
          </div>

          <ul className="luxury-client-nav-menu">
            {menuItems.map(item => (
              <li
                key={item.id}
                className={`luxury-client-nav-item ${activeSection === item.id ? "active" : ""
                  }`}
                onClick={() => handleNavigation(item.id)}
              >
                <div className="client-nav-item-content">
                  <div className="client-nav-icon-wrapper">
                    {item.icon}
                  </div>
                  <div className="client-nav-text">
                    <span className="client-nav-label">{item.label}</span>
                    {item.badge && (
                      <span className="client-nav-badge">{item.badge}</span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </nav>

        {/* Sidebar Footer */}
        <div className="luxury-client-sidebar-footer">
          <div className="client-footer-content">
            <div className="client-footer-actions">
              <button
                className="client-back-site-btn"
                onClick={() => navigate("/")}
              >
                <FiHome className="btn-icon" size={16} />
                <span>Home</span>
              </button>

              <button
                className="client-logout-btn"
                onClick={handleLogout}
              >
                <FiLogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* ======================
         MAIN CONTENT (WHITE MODE)
      ====================== */}
      <main className="luxury-client-dashboard-main">
        <div className="luxury-client-content-wrapper">

          {/* ======================
             BREADCRUMB & NOTIFICATIONS
          ====================== */}
          <div className="luxury-client-breadcrumb-wrapper">
            {/* Left - Breadcrumb */}
            <div className="luxury-client-breadcrumb">
              <span className="client-breadcrumb-item">Client Portal</span>
              <FiChevronRight size={12} />
              <span className="client-breadcrumb-item active">
                {menuItems.find(item => item.id === activeSection)?.label || "Appointments"}
              </span>
            </div>

            {/* Right - Quick Actions */}
            <div className="luxury-client-quick-actions">

              <div
                className="client-notification-indicator"
                onClick={() => {
                  setActiveSection("calendar");
                  sessionStorage.setItem("user_active_section", "calendar");
                  navigate("/user/dashboard", { replace: true });
                }}
                title="Go to Calendar"
              >
                <FiBell size={20} />
                {notificationCount > 0 && (
                  <span className="client-notification-badge">
                    {notificationCount}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ======================
             DYNAMIC CONTENT
          ====================== */}
          <div className="luxury-client-content-container">
            {activeSection === "appointments" && (
              <UserAppointments defaultExpandedId={openAppointmentId} />
            )}

            {activeSection === "calendar" && <Calendar />}

            {activeSection === "cases" && <UserCases />}
            {activeSection === "hearings" && <UserHearings />}
            {/* {activeSection === "chat" && <ChatApp />} */}
            {activeSection === "profile" && <UserProfile />}
            {activeSection === "agreements" && (
              <UserAgreements
                onSign={(agreementId) => {
                  setOpenAgreementId(agreementId);
                  setActiveSection("signAgreement");
                }}
              />
            )}

            {/* 🔥 SIGN PAGE INSIDE DASHBOARD */}
            {activeSection === "signAgreement" && (
              <UserAgreementSign agreementId={openAgreementId} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}